(ns priorities.events
  (:require
    [very.re-frame.core :as rf]
    [graphql.core :as qu]
    [util.core :refer [to-iso deep-merge]]))

(def default-priorities
  [{:id 0
    :title "Saving for a major purchase"
    :icon "piggy"}

   {:id 1
    :title "Planning for my retirement"
    :icon "egg"}

   {:id 2
    :title "Saving for college"
    :icon "graduate"}

   {:id 3
    :title "Managing my investments"
    :icon "pie"}

   {:id 4
    :title "Pets"
    :icon "custom"}

   {:id 5
    :title "Managing my budget"
    :icon "chart"}

   {:id 6
    :title "Minimizing my taxes"
    :icon "scissors"}

   {:id 7
    :title "Ensuring I have appropriate insurance coverage"
    :icon "shield"}

   {:id 8
    :title "Providing a legacy for my family"
    :icon "paper-pen"}

   {:id 9
    :title "Caring for aging parents"
    :icon "hand-heart"}

   {:id 10
    :title "Giving to charity"
    :icon "gift"}

   {:id 11
    :title "Wealth accumulation"
    :icon "hands-gem"}])

(def *maximum-selectable-priorities* 5)

(def *max-goals-per-priority* 10)

(def *max-custom-priorities* 5)

(defn- default-priority? [x]
  (= :default (:type x)))

(defn- custom-priority? [x]
  (= :custom (:type x)))

(defn- hydrate-priorities
  "Hydrate our priorities with some stateful data that makes our life in the
  form manipulation phase a whole lot easier. This includes things like
  selected?, type, order, and goals (which are a combination of temporary and
  server state).

  We have the default priorities, the user's chosen priorities (some of which
  may be custom, some of which may be chosen from the set of defaults), as well
  as the stateful 'not yet submitted to the server' version of the priority.

  We distinguish between the default and custom by assigning a type based on
  whether or not a priority exactly matches the definition of one of the default
  priorities (which for now is a good enough check).

  When hydrating priorities, if a custom-defined priority is identical to a
  default type, we also mark that priority selected, per business rules. We also
  order priorities according to Default > Custom.

  Hydration occurs in layers: default + initial state + updated state = new
  state.
  "
  [default ps stateful]
  (let [hydrated-default (reduce (fn [m [p idx]]
                                   (let [overlap (first (filter #(= (:title %) (:title p)) ps))]
                                     (assoc m
                                             (:id p)
                                             (merge {:selected? (boolean overlap)
                                                     :showing-goals? false
                                                     :type :default
                                                     :list-order idx
                                                     :order (or (:order overlap) idx)
                                                     :editing? false
                                                     :goals (or (:goals overlap) [])} p))))
                                 {}
                                 (map vector default (range)))
        ;; all custom priorities that don't belong to the default set
        custom-not-default (remove (fn [p] (first (filter #(= (:title %) (:title p)) default))) ps)
        hydrated-custom (reduce (fn [m [p idx]]
                                  (assoc m
                                          (:id p)
                                          (merge {:selected? true
                                                  :showing-goals? false
                                                  :type :custom
                                                  :list-order idx
                                                  :editing? false} p)))
                                {}
                                ;; assign an index order to the custom
                                ;; priorities starting at the last index of the
                                ;; default
                                (map vector
                                     custom-not-default
                                     (range (inc (count hydrated-default)) js/Infinity)))]
    (deep-merge hydrated-default hydrated-custom stateful)))

(defn- order-priorities
  "After selecting priorities, reshape the data so we can render the priorities
  in the proper order (if an order already exists on some/all of the selected),
  as well as assign any just-added properties a proper order (which will be
  all the slots AFTER any existing ordered priorities)."
  [ps]
  (let [[created existing] (split-with (fn [x] (or (:temporary? x)
                                                   (default-priority? x))) ps)
        order-sorted (concat (sort-by :order existing) created)]
    (reduce (fn [coll [x idx]]
              (conj coll {:id (:id x)
                          :order idx
                          :title (:title x)
                          :goals (:goals x)}))
            []
            (map vector order-sorted (range)))))

(defn- count-selected [ps]
  (reduce (fn [sum p] (+ sum (if (:selected? p) 1 0))) 0 ps))

(defn- count-custom [ps]
  (reduce (fn [sum p] (+ sum (if (custom-priority? p) 1 0))) 0 (vals ps)))

(defn- toggle-goals
  ([db p]
   (toggle-goals db p (not (get-in db [:financial-priorities :form :selected (:id p) :showing-goals?]))))
  ([db p toggle]
   (assoc-in db [:financial-priorities :form :selected (:id p) :showing-goals?] toggle)))

(def vconj (fnil conj []))

(defn- tmp-goal [g]
  (merge g {:id (random-uuid)
            :target-date (if (empty? (:target-date g)) nil (:target-date g))}))

(defn priorities [db]
  (get-in db [:financial-priorities :priorities]))

(rf/reg-sub
  :financial-priorities/sorted
  :<- [:priorities]
  (fn [ps]
    (sort-by :order ps)))

(defn- form-state [db]
  (get-in db [:financial-priorities :form]))

(defn- stateful-priorities [form]
  (or (:priorities form) {}))

(rf/reg-sub
  :financial-priorities/form-state
  form-state)

(rf/reg-sub
  :financial-priorities/stateful-priorities
  :<- [:priorities]
  :<- [:financial-priorities/form-state]
  (fn [[ps form]]
    (let [stateful (stateful-priorities form)]
      (hydrate-priorities default-priorities ps stateful))))

(rf/reg-sub
  :financial-priorities/form
  :<- [:financial-priorities/form-state]
  :<- [:financial-priorities/stateful-priorities]
  (fn [[form state]]
    (let [selected (count-selected (vals state))
          custom (count-custom state)]
      {:priorities (sort-by :list-order (vals state))
       :action (get form :action :selecting)
       :editing? (get form :editing? false)
       :max-selected? (= selected *maximum-selectable-priorities*)
       :can-add-priority? (< custom *max-custom-priorities*)
       :at-least-one-selected? (< 0 selected)})))

(rf/reg-sub
  :financial-priorities/selected
  :<- [:financial-priorities/form-state]
  (fn [form]
    (sort-by :order (vals (:selected form)))))

(rf/reg-event-fx
  :financial-priorities/reset
  (fn [cfx]
    {:db (update (:db cfx) :financial-priorities dissoc :form)}))

(rf/reg-event-fx
  :financial-priorities/add-priority-goal
  (fn [cfx [p]]
    {:modal {:open 'priorities.views/add-goal-modal}
     :db (assoc-in (:db cfx) [:financial-priorities :form :priority-under-edit] (:id p))}))

(rf/reg-event-fx
  :financial-priorities/cancel-add-priority-goal
  (fn [cfx]
    {:modal {:close 'priorities.views/add-goal-modal}
     :db (update-in (:db cfx) [:financial-priorities :form] dissoc :priority-under-edit)}))

(rf/reg-event-fx
  :financial-priorities/add-priority-goal-confirmed
  (fn [cfx [goal]]
    (let [id (get-in cfx [:db :financial-priorities :form :priority-under-edit])
          p (get-in cfx [:db :financial-priorities :form :selected id])]
      {:modal {:close 'priorities.views/add-goal-modal}
       :db (-> (:db cfx)
               (update-in [:financial-priorities :form] dissoc :priority-under-edit)
               (update-in [:financial-priorities :form :selected id :goals] #(sort-by (juxt :target-date :title) (vconj % (tmp-goal goal))))
               (#(toggle-goals % p true)))})))

(rf/reg-event-db
  :financial-priorities/delete-priority-goal
  (fn [db [p g]]
    (-> db
        (update-in [:financial-priorities :form :selected (:id p) :goals]
                   (fn [gs] (remove #(= (:id %) (:id g)) gs)))
        (#(if (= 0 (count (get-in % [:financial-priorities :form :selected (:id p) :goals])))
            (toggle-goals % p false)
            %)))))

(rf/reg-event-db
  :financial-priorities/priorities-selected
  (fn [db [stateful-priorities]]
    ;; our priorities at this point are "minted" with their existing state so we
    ;; can continue the form and attach goals / reorder priorities, etc.
    (let [selected (filter :selected? stateful-priorities)
          ;; sort by any existing order, then reassign orders to start at 0 and
          ;; increment by 1. note that default priorities may not have an order,
          ;; and in that case we don't want them to be sorted first
          [ordered unordered] (split-with #(contains? % :order) selected)
          reordered (reduce (fn [m [v idx]] (assoc m (:id v) (assoc v :order idx)))
                            {}
                            (map vector
                                 (concat (sort-by :order ordered) unordered)
                                 (range)))]
      (-> db
          (assoc-in [:financial-priorities :form :action] :editing)
          (assoc-in [:financial-priorities :form :selected] reordered)))))

(rf/reg-event-db
  :financial-priorities/change-selected
  (fn [db]
    (-> db
      (assoc-in [:financial-priorities :form :action] :selecting)
      (assoc-in [:financial-priorities :form :selected] {}))))

(rf/reg-event-db
  :financial-priorities/toggle-priority
  (fn [db [p stateful-priorities]]
    (let [selected (count-selected stateful-priorities)]
      (if (:selected? p)
        (assoc-in db [:financial-priorities :form :priorities (:id p) :selected?] false)
        (if (< selected *maximum-selectable-priorities*)
          (assoc-in db [:financial-priorities :form :priorities (:id p) :selected?] true)
          db)))))

(defn- at-max-priorities? [ps]
  (>= (count (filter custom-priority? ps)) *max-custom-priorities*))

(rf/reg-event-db
  :financial-priorities/add-priority
  (fn [db [stateful-priorities]]
    (when-not (at-max-priorities? stateful-priorities)
      (let [tmp {:id (random-uuid)
                 :selected? false
                 :title ""
                 :editing? true
                 :type :custom
                 :list-order (inc (count stateful-priorities))
                 :order (inc (count stateful-priorities))}]
        (assoc-in db [:financial-priorities :form :priorities (:id tmp)] tmp)))))

(rf/reg-event-db
  :financial-priorities/delete-priority
  (fn [db [p]]
    (update-in db [:financial-priorities :form :priorities] dissoc (:id p))))

(rf/reg-event-db
  :financial-priorities/confirm-add-priority
  (fn [db [p title]]
    (update-in db [:financial-priorities :form :priorities (:id p)]
               #(assoc % :title title :editing? false))))

(rf/reg-event-db
  :financial-priorities/edit-priority
  (fn [db [p]]
    (assoc-in db [:financial-priorities :form :priorities (:id p) :editing?] true)))

(rf/reg-event-db
  :financial-priorities/cancel-edit-priority
  (fn [db [p]]
    (assoc-in db [:financial-priorities :form :priorities (:id p) :editing?] false)))

(rf/reg-event-db
  :financial-priorities/toggle-goals
  (fn [db [p]]
    (toggle-goals db p)))

(defn- shift-order
  "The current element order is the 'boundary' on which we reorder.
  If the new order crosses the boundary, we have to update the current.
  If the new order doesn't cross the boundary, we can leave the current alone."
  ([curr old nu] (shift-order curr old nu 0 *maximum-selectable-priorities*))
  ([curr old nu min' max']
   (if (or (and (< old curr) (< curr nu))
           (and (> old curr) (> curr nu))
           (= curr nu))
     (if (> old nu)
       (min (inc curr) max')
       (max (dec curr) min'))
     curr)))

(defn- reorder-priorities [db id old new]
  (let [ps (get-in db [:financial-priorities :form :selected])
        updated (assoc (get ps id) :order new)
        reordered (reduce (fn [m [id p]]
                            (assoc m id (update p :order #(shift-order % old new))))
                          {}
                          (dissoc ps id))
        rejoined (assoc reordered id updated)]
    (assoc-in db [:financial-priorities :form :selected] rejoined)))

(rf/reg-event-db
  :financial-priorities/reorder-priorities
  (fn [db [id old new]]
    (reorder-priorities db id old new)))
